import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { accountingApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Journal entries — FR-ACC-02
 * Two-pane: list left, ledger-style debit/credit detail right.
 */
export function JournalEntriesPage() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState('');
  const [showReversalDialog, setShowReversalDialog] = useState(false);

  const listQuery = useQuery({
    queryKey: ['je', 'list'],
    queryFn: () => accountingApi.listJournalEntries({ limit: 200 }),
  });

  const detailQuery = useQuery({
    queryKey: ['je', selected],
    queryFn: () => accountingApi.getJournalEntry(selected!),
    enabled: !!selected,
  });

  const reverseMut = useMutation({
    mutationFn: ({ id, reason, date }: { id: string; reason: string; date?: string }) =>
      accountingApi.reverseJournalEntry(id, { reason, reversalDate: date }),
    onSuccess: (d) => {
      toast.success(`Reversal ${d.reversalEntryNumber} posted on ${d.reversalDate}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['je'] });
      setShowReversalDialog(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredRows, uniqueSources } = useMemo(() => {
    const rows = listQuery.data ?? [];
    const posted = rows.filter((e: any) => e.status === 'posted').length;
    const totalPostedValue = rows
      .filter((e: any) => e.status === 'posted')
      .reduce((s: number, e: any) => s + (parseFloat(e.total_debit) || 0), 0);
    const sources = new Set<string>();
    for (const e of rows) if (e.source_type) sources.add(e.source_type);
    const filtered = sourceFilter ? rows.filter((e: any) => e.source_type === sourceFilter) : rows;
    return {
      kpis: { total: rows.length, posted, draft: rows.length - posted, totalPostedValue },
      filteredRows: filtered,
      uniqueSources: Array.from(sources).sort(),
    };
  }, [listQuery.data, sourceFilter]);

  const je = detailQuery.data;
  const canReverse = je && je.status === 'posted'
    && !je.reversed_by_entry_id
    && !je.reverses_entry_id
    && !je.is_period_close_entry;

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · General ledger"
        title="Journal entries"
        subtitle="Every debit and credit posted to the ledger. Filter by source, drill in for line detail, reverse if needed."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total entries" value={kpis.total} />
        <KpiTile label="Posted" value={kpis.posted} accent="sage" />
        <KpiTile label="Draft" value={kpis.draft}
          accent={kpis.draft > 0 ? 'amber' : 'default'} />
        <KpiTile label="Posted volume" value={`${fmtCurrency(kpis.totalPostedValue)}`} accent="finance" />
      </div>

      {/* Source filter chips */}
      {uniqueSources.length > 0 && (
        <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto border-slate-200">
          <button
            onClick={() => setSourceFilter('')}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              sourceFilter === ''
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            All sources
          </button>
          {uniqueSources.map((s) => (
            <button
              key={s}
              onClick={() => setSourceFilter(s)}
              className={clsx(
                'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                sourceFilter === s
                  ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                  : 'text-slate-600 hover:bg-slate-50'
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Two-pane */}
      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
          <div className="card overflow-hidden border-slate-200">
            {!filteredRows.length ? (
              <EmptyState message="No entries match" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Entry #</th>
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Date</th>
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Description</th>
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Source</th>
                      <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Amount</th>
                      <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredRows.map((e: any, i: number) => (
                      <tr
                        key={e.id}
                        onClick={() => setSelected(e.id)}
                        className={clsx(
                          'cursor-pointer transition-colors',
                          selected === e.id
                            ? 'bg-finance-50'
                            : i % 2 ? 'bg-slate-50/40 hover:bg-slate-50' : 'bg-white hover:bg-slate-50'
                        )}
                      >
                        <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{e.entry_number}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-700 tabular-nums">{e.entry_date}</td>
                        <td className="px-4 py-2 text-slate-900 max-w-xs truncate">{e.description}</td>
                        <td className="px-4 py-2 text-xs text-slate-600">{e.source_type ?? <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                          ${parseFloat(e.total_debit).toFixed(2)}
                        </td>
                        <td className="px-4 py-2">
                          <JeStatusBadge status={e.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card p-5 min-h-[500px] border-slate-200">
            {!selected ? (
              <EmptyState
                message="Select an entry"
                hint="Click a row on the left to view its debit and credit lines."
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                }
              />
            ) : detailQuery.isLoading ? (
              <LoadingState rows={4} />
            ) : je ? (
              <JournalEntryDetail
                je={je}
                canReverse={!!canReverse}
                onReverse={() => setShowReversalDialog(true)}
              />
            ) : null}
          </div>
        </div>
      )}

      {showReversalDialog && je && (
        <ReversalDialog
          je={je}
          loading={reverseMut.isPending}
          onClose={() => setShowReversalDialog(false)}
          onConfirm={(reason, date) => reverseMut.mutate({ id: je.id, reason, date })}
        />
      )}
    </div>
  );
}

// ============================================================
// JournalEntryDetail — classic ledger presentation
// ============================================================
function JournalEntryDetail({
  je, canReverse, onReverse,
}: { je: any; canReverse: boolean; onReverse: () => void }) {
  const totalDebit = je.lines.reduce((s: number, l: any) => s + parseFloat(l.debit), 0);
  const totalCredit = je.lines.reduce((s: number, l: any) => s + parseFloat(l.credit), 0);

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="eyebrow text-finance-700 mb-1">Journal entry</div>
            <div className="font-mono font-bold text-lg text-slate-900">{je.entry_number}</div>
            <div className="font-mono text-xs text-slate-500 mt-0.5">{je.entry_date}</div>
          </div>
          <JeStatusBadge status={je.status} />
        </div>
        <div className="text-sm text-slate-700 mt-3 leading-relaxed">{je.description}</div>
        {je.source_type && (
          <div className="text-2xs text-slate-500 mt-2 font-mono">
            source: <span className="text-slate-700">{je.source_type}</span>
            {je.source_id && <span className="text-slate-400"> · {String(je.source_id).slice(0, 8)}</span>}
          </div>
        )}
      </div>

      {/* Status banners */}
      {je.is_period_close_entry && (
        <div className="bg-finance-50 border border-finance-200 rounded-xl px-3 py-2 text-xs text-finance-800 mb-3">
          <div className="font-semibold">Period-close entry</div>
          <div className="mt-0.5">Cannot be reversed directly — reopen the period instead.</div>
        </div>
      )}
      {je.reversed_by_entry_id && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 mb-3">
          <div className="font-semibold">Reversed</div>
          <div className="mt-0.5">
            by JE <span className="font-mono">{je.reversed_by_entry_id.slice(0, 8)}…</span>
            {je.reversed_at && <> on {new Date(je.reversed_at).toLocaleDateString()}</>}
          </div>
          {je.reversal_reason && <div className="mt-1 italic">{je.reversal_reason}</div>}
        </div>
      )}
      {je.reverses_entry_id && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs text-slate-700 mb-3">
          <div className="font-semibold">This is a reversal</div>
          <div className="mt-0.5">of JE <span className="font-mono">{je.reverses_entry_id.slice(0, 8)}…</span></div>
        </div>
      )}

      {/* Ledger table */}
      <div className="eyebrow text-slate-500 mb-2">Ledger</div>
      <div className="rounded-lg border border-slate-200 overflow-hidden mb-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Account</th>
              <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Debit</th>
              <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {je.lines.map((l: any) => {
              const debit = parseFloat(l.debit);
              const credit = parseFloat(l.credit);
              return (
                <tr key={l.id} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs font-semibold text-slate-900">{l.account_code}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{l.account_name}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {debit > 0 ? (
                      <span className="text-slate-900">{fmtCurrency(debit)}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {credit > 0 ? (
                      <span className="text-slate-900">{fmtCurrency(credit)}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 border-t-2 border-slate-300">
              <td className="px-3 py-2 eyebrow text-slate-700">Totals</td>
              <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-slate-900">
                ${totalDebit.toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right font-mono font-bold tabular-nums text-slate-900">
                ${totalCredit.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Balance assertion */}
      <div className={clsx(
        'rounded-lg px-3 py-2 text-xs font-medium mb-4',
        Math.abs(totalDebit - totalCredit) < 0.01
          ? 'bg-sage-50 text-sage-800 border border-sage-200'
          : 'bg-danger/10 text-danger border border-danger/30'
      )}>
        {Math.abs(totalDebit - totalCredit) < 0.01 ? (
          <span>✓ Balanced · {je.lines.length} line{je.lines.length === 1 ? '' : 's'}</span>
        ) : (
          <span>⚠ Out of balance by ${Math.abs(totalDebit - totalCredit).toFixed(2)}</span>
        )}
      </div>

      {canReverse && (
        <div className="flex justify-end">
          <button
            className="text-xs font-semibold text-danger hover:bg-red-50 px-3 py-1.5 rounded-pill transition-colors"
            onClick={onReverse}
          >
            Reverse entry
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================
// ReversalDialog — proper modal instead of prompt()
// ============================================================
function ReversalDialog({
  je, loading, onClose, onConfirm,
}: {
  je: any;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string, date?: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [reversalDate, setReversalDate] = useState('');

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Reverse · Accounting</div>
        <h2 className="text-xl font-bold font-display text-slate-900">Reverse journal entry</h2>
        <p className="text-sm text-slate-500 mt-2">
          Creates an opposing entry that zeroes out{' '}
          <span className="font-mono font-semibold text-slate-700">{je.entry_number}</span>.
          The original stays on the ledger, referenced by the new reversal entry.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Reason <span className="text-danger">*</span></label>
          <textarea
            className="input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Invoice was posted to wrong customer account"
          />
        </div>
        <div>
          <label className="label">Reversal date <span className="font-normal text-slate-400 normal-case tracking-normal">(blank = today)</span></label>
          <input
            type="date"
            className="input"
            value={reversalDate}
            onChange={(e) => setReversalDate(e.target.value)}
          />
        </div>
      </div>

      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason, reversalDate || undefined)}
        >
          {loading ? 'Reversing…' : 'Post reversal'}
        </button>
      </div>
    </Modal>
  );
}

function JeStatusBadge({ status }: { status: string }) {
  const cls = status === 'posted'
    ? 'bg-sage-50 text-sage-700 ring-sage-200'
    : 'bg-slate-100 text-slate-600 ring-slate-200';
  return (
    <span className={clsx(
      'inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset',
      cls
    )}>
      {status}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'finance' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-slate-200',
    finance: 'bg-finance-500',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden border-slate-200">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-slate-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
